// Cycle 186 — web Dashboard "Live · next 30 min" focus panel.
//
// WEB-FRAME-ONLY (mounted solely by the web-gated DashboardScreen). A short,
// user-selectable 1h/2h/3h recent window where the cycle-180 forward-projection
// cone has room to breathe — relocated here off the long-range review chart
// (on a 7d/30d window a 30-min cone collapses to a few clipped pixels).
//
// Display-only secondary surface: NO alarm-acknowledgment controls (cycle-73);
// the cone keeps its "projected · not for dosing" label and the cycle-177
// page disclaimer covers it. Tokens only — no hardcoded hex.
import { useMemo, useState } from "react";

import { HistoryChart } from "../../components/glucose/HistoryChart";
import { TrendArrow } from "../../components/glucose/TrendArrow";
import { Tabs } from "../../components/ui/Tabs";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import {
  displayValue,
  trendInfo,
  zoneColor,
  zoneOf,
} from "../../lib/glucose";
import {
  computeProjectionModel,
  isProjectionStale,
  predictedValue,
} from "../../lib/projection";
import type { Alert, Reading, Thresholds, Treatment } from "../../lib/types";
import styles from "./LiveFocusPanel.module.css";

const PROJECT_MINUTES = 30;

type LiveWindowId = "1h" | "2h" | "3h";
const LIVE_WINDOWS: { id: LiveWindowId; label: string; hours: number }[] = [
  { id: "1h", label: "1h", hours: 1 },
  { id: "2h", label: "2h", hours: 2 },
  { id: "3h", label: "3h", hours: 3 },
];

interface Props {
  /** The dashboard's already-loaded readings (full cadence for the last
   *  several hours — the smallest dashboard window is 6h). No new fetch. */
  readings: Reading[];
  treatments: Treatment[];
  alerts: Alert[];
  thresholds: Thresholds;
  units: "mg/dL" | "mmol/L";
  h24: boolean;
  /** The ticking dashboard clock — shared with the chart so the hero's
   *  projection pause and the cone's stale gate agree. */
  nowMs: number;
  /** ⚠ Cycle 435: the hero readout is a `DisplayGlucose` the selector
   *  (`lib/currentGlucose.ts`) released as CURRENT, or null — withheld.
   *  The chart and the projection keep reading `readings` (history); only
   *  the painted number comes from here. A raw `Reading` cannot be
   *  passed. */
  current: DisplayGlucose | null;
}

export function LiveFocusPanel({
  readings,
  treatments,
  alerts,
  thresholds,
  units,
  h24,
  nowMs,
  current,
}: Props) {
  const [windowId, setWindowId] = useState<LiveWindowId>("2h"); // default 2h
  const hours = LIVE_WINDOWS.find((w) => w.id === windowId)?.hours ?? 2;
  const startMs = nowMs - hours * 60 * 60 * 1000;

  // The Live window's readings — sliced from what the dashboard already holds.
  const liveReadings = useMemo(
    () => readings.filter((r) => new Date(r.timestamp).getTime() >= startMs),
    [readings, startMs],
  );

  // SAME model the embedded chart's cone uses (same readings array, same
  // helper), so the hero "in 30 min ~X" can never diverge from the cone end.
  const model = useMemo(
    () => computeProjectionModel(liveReadings),
    [liveReadings],
  );
  const paused = !model || isProjectionStale(model, nowMs, PROJECT_MINUTES);
  const predVal = model && !paused ? predictedValue(model, PROJECT_MINUTES) : null;

  // Hero current readout — cycle 435: the selector's released value, or
  // nothing. It used to be the projection's origin reading (the last of
  // `readings`), greyed when stale; a greyed number is still a number a
  // caregiver reads, so an unprovable value is now withheld outright. The
  // projection cone's own gates are untouched and still read `readings`.
  const valueStale = current == null;
  const zone = current ? zoneOf(current.sgv, thresholds) : "unknown";
  const heroColor = valueStale ? "var(--ink-3)" : zoneColor(zone);
  const trend = current ? trendInfo(current.trend, current.trendRate) : null;
  const predZone = predVal != null ? zoneOf(predVal, thresholds) : "unknown";

  return (
    <section className={styles.panel} aria-label="Live glucose, next 30 minutes">
      <header className={styles.head}>
        <div className={styles.title}>
          <span className={styles.titleMain}>Live</span>
          <span className={styles.tag}>next 30 min</span>
        </div>
        <div className={styles.windowTabs}>
          <Tabs<LiveWindowId>
            tabs={LIVE_WINDOWS.map((w) => ({ id: w.id, label: w.label }))}
            activeId={windowId}
            onChange={setWindowId}
          />
        </div>
      </header>

      <div className={styles.hero}>
        <span className={`${styles.heroValue} tnum`} style={{ color: heroColor }}>
          {current ? displayValue(current.sgv, units) : "—"}
        </span>
        <span className={styles.heroUnit}>{units}</span>
        {trend ? (
          <TrendArrow
            glyph={trend.glyph}
            zone={valueStale ? "unknown" : zone}
            stale={valueStale}
            size="28px"
          />
        ) : null}
        <div className={styles.projHero}>
          <span className={styles.projLabel}>in 30 min</span>
          <span
            className={`${styles.projValue} tnum`}
            style={{ color: paused ? "var(--ink-3)" : zoneColor(predZone) }}
          >
            {paused ? "paused" : `~${displayValue(predVal!, units)}`}
          </span>
        </div>
      </div>

      <div className={styles.chartWrap}>
        <HistoryChart
          readings={liveReadings}
          treatments={treatments}
          alerts={alerts}
          thresholds={thresholds}
          rangeStartMs={startMs}
          rangeEndMs={nowMs}
          nowMs={nowMs}
          height={220}
          units={units}
          h24={h24}
          showTooltips
          // No panSliceMs / pinchZoom: a fixed [now − Nh, now] window pinned at
          // the live edge, so the cycle-180 live-edge gate fires and the cone
          // draws. The 1h/2h/3h control re-points the window above.
          projectForward
          projectMinutes={PROJECT_MINUTES}
        />
      </div>

      <ul className={styles.legend} aria-hidden="true">
        <li>
          <i className={styles.swatch} style={{ background: zoneColor("in_range") }} />
          In range
        </li>
        <li>
          <i className={styles.swatch} style={{ background: zoneColor("high") }} />
          High
        </li>
        <li>
          <i className={styles.swatch} style={{ background: zoneColor("low") }} />
          Low
        </li>
        <li>
          <i className={`${styles.swatch} ${styles.swatchProj}`} />
          Projection (uncertain)
        </li>
      </ul>
    </section>
  );
}
